<template>
  <div class="space-y-4">
    <div v-if="o.cases" class="grid grid-cols-4 gap-4">
      <div class="rounded-md border border-gray-200 bg-white p-4">
        <span class="block text-gray-500">Verdict over {{ o.cases }} shared case{{ o.cases === 1 ? '' : 's' }}</span>
        <span :class="['text-lg font-medium', tone]">{{ verdictText }}</span>
      </div>
      <div class="rounded-md border border-gray-200 bg-white p-4">
        <span class="block text-gray-500">Mean score {{ aName }} → {{ bName }}</span>
        <span class="text-lg text-gray-900 tabular-nums">{{ percent(o.a_mean) }} → {{ percent(o.b_mean) }}</span>
        <span :class="['block tabular-nums', tone]">{{ signedPoints(o.mean_diff) }}</span>
      </div>
      <div class="rounded-md border border-gray-200 bg-white p-4">
        <span class="block text-gray-500">95% interval of the difference</span>
        <span v-if="o.ci95" class="text-lg text-gray-900 tabular-nums">{{ signedPoints(o.ci95[0]) }} to {{ signedPoints(o.ci95[1]) }}</span>
        <span v-else class="text-lg text-gray-500">needs {{ o.min_cases }}+ cases</span>
        <span class="block text-gray-500 tabular-nums">p = {{ o.p }} (paired over cases)</span>
      </div>
      <div class="rounded-md border border-gray-200 bg-white p-4">
        <span class="block text-gray-500">Cases {{ bName }} scored higher / lower / same</span>
        <span class="text-lg text-gray-900 tabular-nums">{{ o.wins }} / {{ o.losses }} / {{ o.ties }}</span>
      </div>
    </div>
    <p v-else class="text-gray-500">No case has a score on both sides.</p>
    <p v-if="(o.only_in_a || []).length || (o.only_in_b || []).length" class="text-gray-500">
      Not compared: {{ [...(o.only_in_a || []), ...(o.only_in_b || [])].join(', ') }} ran on one side only.
    </p>
    <div v-if="(comparison.cases || []).length" class="rounded-md border border-gray-200 overflow-hidden bg-white">
      <table class="w-full table-fixed">
        <colgroup><col style="width:34%"><col style="width:13%"><col style="width:13%"><col style="width:10%"><col style="width:12%"><col style="width:18%"></colgroup>
        <thead class="bg-gray-50"><tr>
          <th :class="TH">Case</th><th :class="[TH, 'text-right']">{{ aName }}</th><th :class="[TH, 'text-right']">{{ bName }}</th>
          <th :class="[TH, 'text-right']">Δ</th><th :class="TH">Call</th><th :class="TH">Criteria that moved</th>
        </tr></thead>
        <tbody class="divide-y divide-gray-200">
          <tr v-for="c in comparison.cases" :key="c.case">
            <td :class="TD"><span class="font-medium">{{ c.label }}</span></td>
            <td :class="[TD, 'text-right tabular-nums']">{{ percent(c.a_mean) }}<span v-if="c.a_n > 1" class="text-gray-500"> ±{{ Math.round(c.a_sd * 100) }} ({{ c.a_n }})</span></td>
            <td :class="[TD, 'text-right tabular-nums']">{{ percent(c.b_mean) }}<span v-if="c.b_n > 1" class="text-gray-500"> ±{{ Math.round(c.b_sd * 100) }} ({{ c.b_n }})</span></td>
            <td :class="[TD, 'text-right tabular-nums', c.diff > 0 ? 'text-green-700' : c.diff < 0 ? 'text-red-700' : 'text-gray-500']">{{ signedPoints(c.diff) }}</td>
            <td :class="TD"><span :class="c.call === 'B better' ? 'text-green-700' : c.call === 'A better' ? 'text-red-700' : 'text-gray-500'">{{ callText(c) }}</span><span v-if="c.p !== null" class="block text-gray-500 tabular-nums">p = {{ c.p }}</span></td>
            <td :class="TD" class="text-gray-700">
              <span v-for="f in c.criterion_flips" :key="f.id" class="block">{{ f.id }} {{ percent(f.a) }} → {{ percent(f.b) }}</span>
              <span v-if="!c.criterion_flips.length" class="text-gray-500">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { TD, TH } from '../hermes';
import { percent, signedPoints } from './bench';

export default {
  name: 'CompareTable',
  props: {
    comparison: { type: Object, required: true },
    aName: { type: String, default: 'A' },
    bName: { type: String, default: 'B' },
  },
  data() {
    return { TD, TH };
  },
  computed: {
    o() {
      return this.comparison.overall || {};
    },
    verdictText() {
      if (this.o.verdict === 'B beats A') return `${this.bName} is better`;
      if (this.o.verdict === 'A beats B') return `${this.aName} is better`;
      if (this.o.verdict === 'too few cases') return `Too few cases to decide (needs ${this.o.min_cases})`;
      return 'No significant difference';
    },
    tone() {
      if (this.o.verdict === 'B beats A') return 'text-green-700';
      if (this.o.verdict === 'A beats B') return 'text-red-700';
      return 'text-gray-700';
    },
  },
  methods: {
    percent,
    signedPoints,
    callText(c) {
      if (c.call === 'B better') return `${this.bName} better`;
      if (c.call === 'A better') return `${this.aName} better`;
      return c.a_n > 1 && c.b_n > 1 ? 'no clear difference' : 'one run each';
    },
  },
};
</script>
